import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, DollarSign, TrendingDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ExpenseForm from "@/components/expenses/ExpenseForm";
import ExpenseList from "@/components/expenses/ExpenseList";
import ExpenseStats from "@/components/expenses/ExpenseStats";

const ExpenseTracking: React.FC = () => {
  const { t } = useTranslation();
  const { user, hasPermission } = useAuth();
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (hasPermission("manage_users")) {
      fetchExpenses();
      fetchCategories();
    }
  }, [hasPermission]);

  const fetchExpenses = async () => {
    try {
      const { data: shopData } = await supabase
        .from("user_repair_shops")
        .select("repair_shop_id")
        .eq("user_id", user?.id)
        .single();

      if (!shopData) return;

      const { data, error } = await supabase
        .from("expenses")
        .select("*, expense_categories(name)")
        .eq("repair_shop_id", shopData.repair_shop_id)
        .order("expense_date", { ascending: false });

      if (error) throw error;
      setExpenses(data || []);
    } catch (error) {
      console.error("Error fetching expenses:", error);
      toast({
        title: t("common.error"),
        description: t("expenses.fetchError"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data: shopData } = await supabase
        .from("user_repair_shops")
        .select("repair_shop_id")
        .eq("user_id", user?.id)
        .single();

      if (!shopData) return;

      const { data, error } = await supabase
        .from("expense_categories")
        .select("*")
        .eq("repair_shop_id", shopData.repair_shop_id);

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  if (!hasPermission("manage_users")) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              {t("expenses.accessDenied")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">{t("expenses.title")}</h1>
          <p className="text-muted-foreground">{t("expenses.subtitle")}</p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t("expenses.addExpense")}
        </Button>
      </div>

      <ExpenseStats expenses={expenses} />

      <ExpenseList
        expenses={expenses}
        onRefresh={fetchExpenses}
        loading={loading}
      />

      {showForm && (
        <ExpenseForm
          categories={categories}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            fetchExpenses();
            setShowForm(false);
          }}
        />
      )}
    </div>
  );
};

export default ExpenseTracking;
